import { createAsyncThunk } from '@reduxjs/toolkit';
import { pathOr } from 'ramda';

import { DEFAULT_UNDERGROUND_TRAVEL_TIME } from '@/packages/Filters/shared/constants/underground';
import { JsonQuery } from '@/packages/JsonQuery';
import { needClearGeo } from '@/packages/JsonQuery/utils/needClearGeo';
import { EMetroTravelType, type IJsonQueryGeo } from '@/packages/api-models/common/json_query';

import { type TThunkApi } from '@/microfrontends/filters/shared/store';
import { geoTagsActions } from '@/microfrontends/filters/shared/store/slices/geoTags';
import { jsonQueryActions } from '@/microfrontends/filters/shared/store/slices/jsonQuery';
import { type TGeoObject } from '@/microfrontends/filters/shared/types/LEGACY/geoSuggest';
import { type TLocation } from '@/microfrontends/filters/shared/types/location';
import {
  circlesAreEqual,
  coordsArrayNumberToString,
  coordsIsEqual,
  coordsNumberToString,
} from '@/microfrontends/filters/shared/utils/geo';
import { tempNormalizeLocation } from '@/microfrontends/filters/shared/utils/tempNormalizeLocation';

import { isICircleObject, isIPolygonObject } from './helpers';

interface IPayload {
  geoObject: TGeoObject;
  location: TLocation;
}

export const selectGeoAction = createAsyncThunk<void, IPayload, TThunkApi>(
  'geo/selectGeoAction',
  async (payload, thunkApi) => {
    const { getState, dispatch } = thunkApi;
    const { geoObject } = payload;
    const state = getState();

    const nextJsonQuery = new JsonQuery(state.jsonQuery.current);
    const geo: IJsonQueryGeo = {
      type: 'geo',
      value: pathOr([], ['geo', 'value'], state.jsonQuery.current).slice(),
    };

    nextJsonQuery.setVillage(null);

    (function geoSelected(): void {
      if (!isIPolygonObject(geoObject) && !isICircleObject(geoObject)) {
        if (geo.value.filter(geoValue => geoValue.type === geoObject.type && geoValue.id === geoObject.id).length > 0) {
          return;
        }

        if (geoObject.locationInfo && needClearGeo([geoObject.locationInfo], tempNormalizeLocation(payload.location))) {
          geo.value = [];
        }

        switch (geoObject.type) {
          case 'location':
            if (!geoObject.locationInfo || geoObject.locationInfo.id !== geoObject.id) {
              const currentRegion = nextJsonQuery.getTermsValue('region');

              if (!currentRegion || !currentRegion.includes(geoObject.regionId)) {
                nextJsonQuery.setRegion([geoObject.regionId]);
                geo.value = [];
              }

              geo.value.push({
                type: geoObject.type,
                id: geoObject.id,
              });

              dispatch(geoTagsActions.add([{ type: 'location', id: geoObject.id, name: geoObject.text }]));
            }
            break;
          case 'street':
            geo.value.push({
              type: geoObject.type,
              id: geoObject.id,
            });

            dispatch(geoTagsActions.add([{ type: 'street', id: geoObject.id, name: geoObject.text }]));
            break;
          case 'highway':
            geo.value.push({
              type: geoObject.type,
              id: geoObject.id,
            });
            break;
          case 'district':
            geo.value.push({
              type: geoObject.type,
              id: geoObject.id,
            });

            dispatch(geoTagsActions.add([{ type: 'district', id: geoObject.id, name: geoObject.text }]));
            break;
          case 'underground':
            geo.value.push({
              type: geoObject.type,
              id: geoObject.id,
            });

            // TODO Дополнять из другой апишки, у станций нет цвета и инфы о реконструкции
            dispatch(
              geoTagsActions.add([
                {
                  type: 'underground',
                  id: geoObject.id,
                  name: geoObject.text,
                  color: null,
                  releaseYear: null,
                  underConstruction: false,
                },
              ]),
            );
            break;
          case 'house':
            geo.value.push({
              type: geoObject.type,
              id: geoObject.id,
            });

            dispatch(geoTagsActions.add([{ type: 'house', id: geoObject.id, name: geoObject.text }]));
            break;
          case 'newobject':
            geo.value.push({
              type: geoObject.type,
              id: geoObject.id,
            });

            dispatch(geoTagsActions.add([{ type: 'newobject', id: geoObject.id, name: geoObject.text }]));
            break;
          case 'railway':
            geo.value.push({
              type: geoObject.type,
              id: geoObject.id,
            });

            dispatch(geoTagsActions.add([{ type: 'railway', id: geoObject.id, name: geoObject.text }]));
            break;
          case 'regional_district_id':
            geo.value.push({
              type: geoObject.type,
              id: geoObject.id,
            });

            dispatch(geoTagsActions.add([{ type: 'regionalDistrict', id: geoObject.id, name: geoObject.text }]));
            break;
          default:
            break;
        }
      } else if (isIPolygonObject(geoObject)) {
        if (
          geo.value.filter(
            geoValue =>
              geoValue.type === 'polygon' &&
              coordsIsEqual(
                geoValue.coordinates,
                geoObject.polygon ? coordsArrayNumberToString(geoObject.polygon) : [],
              ),
          ).length > 0
        ) {
          return;
        }

        geo.value.push({
          type: 'polygon',
          name: geoObject.text,
          coordinates: geoObject.polygon ? coordsArrayNumberToString(geoObject.polygon) : [],
        });
      } else if (isICircleObject(geoObject)) {
        if (
          geo.value.filter(
            geoValue =>
              geoValue.type === 'distance' &&
              circlesAreEqual(geoValue, { radius: geoObject.radius, center: coordsNumberToString(geoObject.center) }),
          ).length > 0
        ) {
          return;
        }

        geo.value.push({
          type: 'distance',
          name: geoObject.text,
          radius: geoObject.radius,
          center: coordsNumberToString(geoObject.center),
        });
      }

      if (geoObject.type === 'underground') {
        const wasNoUndergrounds = !nextJsonQuery.getGeoValueByType('underground');
        const wasNoTravelType = !nextJsonQuery.getTermValue('only_foot');
        const wasNoTravelTime = !nextJsonQuery.getRangeValue('foot_min');

        if (wasNoUndergrounds && wasNoTravelTime && wasNoTravelType) {
          nextJsonQuery.setUndergroundTravelType(EMetroTravelType.MapFoot);
          nextJsonQuery.setUndergroundTravelTime(DEFAULT_UNDERGROUND_TRAVEL_TIME);
        }
      }
    })();

    nextJsonQuery.setGeo(geo.value);

    dispatch(jsonQueryActions.change(nextJsonQuery.toJSON()));
  },
);
